import SyosGridRow from './SyosGridRow';
import helpers from './SyosGridHelpers';
import type SyosSeat from '../SyosSeat';

const addToTabOrder = ($el: Element) => {
  $el.setAttribute('tabindex', '0');
  $el.setAttribute('focusable', 'true');
};

const removeFromTabOrder = ($el: Element) => {
  $el.setAttribute('tabindex', '-1');
  $el.removeAttribute('focusable');
};

class SyosGrid {
  rows: Map<number, SyosGridRow>;

  rowIndexes: number[];

  activeDescendant: Element | undefined;

  constructor(private $container: HTMLElement, seats: SyosSeat[]) {
    this.rows = new Map();

    seats.forEach((seat) => {
      this.addSeat(seat);

      seat.setGrid(this);
    });

    this.rowIndexes = Array.from(this.rows.keys())
      .sort(helpers.numericSort);

    this.rows.forEach((row) => row.finalize());

    const firstSeat = this.getFirstSeat();

    if (firstSeat) {
      this.setActiveDesc(firstSeat.$el);
    }

    this.$container.addEventListener('focusin', this.onFocusIn);
  }

  onFocusIn = (event: FocusEvent) => {
    this.setActiveDesc(event.target as Element);
  };

  setActiveDesc = ($el: Element) => {
    if ($el === this.activeDescendant) {
      return;
    }

    if (this.activeDescendant) {
      removeFromTabOrder(this.activeDescendant);
    }

    addToTabOrder($el);

    this.activeDescendant = $el;
  };

  addSeat(seat: SyosSeat) {
    let row = this.rows.get(seat.gridRow);

    if (!row) {
      row = new SyosGridRow();
      this.rows.set(seat.gridRow, row);
    }

    row.addSeat(seat);
  }

  getFirstSeat() {
    const row = this.getFirstRow();

    if (row) {
      return row.getFirstSeat();
    }

    return undefined;
  }

  getLastSeat() {
    return this.getLastRow().getLastSeat();
  }

  getFirstRow() {
    return this.rows.get(this.rowIndexes[0]);
  }

  getLastRow() {
    return this.rows.get(this.rowIndexes[this.rowIndexes.length - 1])!;
  }

  moveLeft(seat: SyosSeat) {
    const newSeat = this.rows.get(seat.gridRow)
      .getPreviousSeat(seat);

    if (newSeat) {
      newSeat.focus();
    }
  }

  moveRight(seat: SyosSeat) {
    const newSeat = this.rows.get(seat.gridRow)
      .getNextSeat(seat);

    if (newSeat) {
      newSeat.focus();
    }
  }

  moveUp(seat: SyosSeat) {
    const previousRowNumber = helpers.getPreviousElement(this.rowIndexes, seat.gridRow);

    if (previousRowNumber === null) {
      return;
    }

    this.rows.get(previousRowNumber)
      .getNearestSeat(seat)
      .focus();
  }

  moveDown(seat: SyosSeat) {
    const nextRowNumber = helpers.getNextElement(this.rowIndexes, seat.gridRow);

    if (nextRowNumber === null) {
      return;
    }

    this.rows.get(nextRowNumber)
      .getNearestSeat(seat)
      .focus();
  }

  moveToFirstRow(seat: SyosSeat) {
    this.getFirstRow()
      .getNearestSeat(seat)
      .focus();
  }

  moveToLastRow(seat: SyosSeat) {
    this.getLastRow()
      .getNearestSeat(seat)
      .focus();
  }

  moveToEndOfRow(seat: SyosSeat) {
    this.rows.get(seat.gridRow)
      .getLastSeat()
      .focus();
  }

  moveToStartOfRow(seat: SyosSeat) {
    this.rows.get(seat.gridRow)
      .getFirstSeat()
      .focus();
  }

  moveToFirstSeat() {
    this.getFirstSeat()
      .focus();
  }

  moveToLastSeat() {
    this.getLastSeat()
      .focus();
  }

  destroy() {
    this.$container.removeEventListener('focusin', this.onFocusIn);

    this.$container = undefined;

    this.rows = undefined;

    this.activeDescendant = undefined;
  }
}

export default SyosGrid;
